import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { Pane, Popover, Position, Text, TextInputField } from "evergreen-ui";
import { Store } from "../../../../common/models";
import { FilterExpressionType, SortOrder } from "../../../../common/query-filters";
import { StoreForAdmin } from "../../../../common/tables/store/StoreForAdmin";
import { queryStoresForAdmin } from "../../datastore/StoreDataStore";
import { ColorPalette } from "../../context/Theme";

export interface StorePickerProps {
    label?: string;
    value: string;
    onSelect: (storeId: string, storeName: string) => void;
    placeholder?: string;
    required?: boolean;
    width?: string;
    className?: string;
}

const SEARCH_DEBOUNCE_MS = 300;
const BLUR_DELAY_MS = 150;
const PAGE_SIZE = 20;

type DropdownState =
    | { kind: "idle" }
    | { kind: "loading" }
    | { kind: "error" }
    | { kind: "results"; stores: Store[] };

export const StorePicker = (props: StorePickerProps): JSX.Element => {
    const {
        label = "Store",
        value,
        onSelect,
        placeholder,
        required = false,
        width = "620px",
        className = "",
    } = props;

    const [inputValue, setInputValue] = useState<string>(value);
    const [isDropdownOpen, setIsDropdownOpen] = useState<boolean>(false);
    const [state, setState] = useState<DropdownState>({ kind: "idle" });
    const [triggerWidthPx, setTriggerWidthPx] = useState<number>(0);
    const [highlightedIndex, setHighlightedIndex] = useState<number>(-1);
    const triggerRef = useRef<HTMLDivElement>(null);
    const hasSelectedRef = useRef<boolean>(false);
    const searchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const blurTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    // Keep the dropdown pinned to the actual rendered width of the text input,
    // since the input respects maxWidth but may settle below it based on
    // available space.
    useLayoutEffect(() => {
        const el = triggerRef.current;
        if (!el) {
            return;
        }
        const measure = () => setTriggerWidthPx(el.clientWidth);
        measure();
        const observer = new ResizeObserver(measure);
        observer.observe(el);
        return () => observer.disconnect();
    }, []);

    // Keep the input in sync when the committed value changes from the parent
    // (e.g. after the form saves successfully or on initial load).
    useEffect(() => {
        setInputValue(value);
    }, [value]);

    useEffect(() => {
        return () => {
            if (searchTimeoutRef.current) {
                clearTimeout(searchTimeoutRef.current);
            }
            if (blurTimeoutRef.current) {
                clearTimeout(blurTimeoutRef.current);
            }
        };
    }, []);

    const runSearch = useCallback((substring: string) => {
        if (!substring.trim()) {
            setState({ kind: "idle" });
            setHighlightedIndex(-1);
            return;
        }
        setState({ kind: "loading" });
        setHighlightedIndex(-1);
        if (searchTimeoutRef.current) {
            clearTimeout(searchTimeoutRef.current);
        }
        searchTimeoutRef.current = setTimeout(() => {
            queryStoresForAdmin({
                expressions: [
                    {
                        columnName: "name" as keyof StoreForAdmin,
                        columnValue: [substring],
                        type: FilterExpressionType.contains,
                        caseInsensitive: true,
                    },
                    {
                        columnName: "internalVendorName" as keyof StoreForAdmin,
                        columnValue: [],
                        type: FilterExpressionType.isNotNull,
                    },
                ],
                sortColumn: "name" as keyof StoreForAdmin,
                sortOrder: SortOrder.ASCENDING,
                page: 0,
                pageSize: PAGE_SIZE,
            }).then((stores) => {
                setState({ kind: "results", stores });
                setHighlightedIndex(stores.length > 0 ? 0 : -1);
            }).catch(() => {
                setState({ kind: "error" });
            });
        }, SEARCH_DEBOUNCE_MS);
    }, []);

    const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        const nextValue = e.target.value;
        setInputValue(nextValue);
        setIsDropdownOpen(true);
        hasSelectedRef.current = false;
        runSearch(nextValue);
    }, [runSearch]);

    const handleFocus = useCallback(() => {
        setIsDropdownOpen(true);
        // Only search if the user has typed a filter substring that differs
        // from the committed value. Focusing an already-populated field
        // shouldn't fire a redundant fetch for the committed store name.
        if (inputValue.trim() && inputValue !== value) {
            runSearch(inputValue);
        }
    }, [inputValue, value, runSearch]);

    const handleClick = useCallback(() => {
        setIsDropdownOpen(true);
    }, []);

    const handleBlur = useCallback(() => {
        if (blurTimeoutRef.current) {
            clearTimeout(blurTimeoutRef.current);
        }
        // Delay so an onSelect click on a menu item lands before we close the dropdown.
        blurTimeoutRef.current = setTimeout(() => {
            setIsDropdownOpen(false);
            if (!hasSelectedRef.current) {
                // User blurred without picking anything — revert to the committed value.
                // Crucially, we do NOT call onSelect here, so the parent form stays clean.
                setInputValue(value);
            }
            hasSelectedRef.current = false;
        }, BLUR_DELAY_MS);
    }, [value]);

    const handleItemSelect = useCallback((store: Store) => {
        hasSelectedRef.current = true;
        setInputValue(store.name);
        setIsDropdownOpen(false);
        setState({ kind: "idle" });
        setHighlightedIndex(-1);
        onSelect(store.id, store.name);
    }, [onSelect]);

    const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === "Escape") {
            // Blur the input so handleBlur's revert-to-committed-value path
            // runs, rather than leaving the typed substring in place.
            e.currentTarget.blur();
            return;
        }
        if (state.kind !== "results" || state.stores.length === 0) {
            return;
        }
        if (e.key === "ArrowDown") {
            e.preventDefault();
            setHighlightedIndex((prev) => Math.min(prev + 1, state.stores.length - 1));
        } else if (e.key === "ArrowUp") {
            e.preventDefault();
            setHighlightedIndex((prev) => Math.max(prev - 1, 0));
        } else if (e.key === "Enter") {
            if (highlightedIndex >= 0 && highlightedIndex < state.stores.length) {
                e.preventDefault();
                handleItemSelect(state.stores[highlightedIndex]);
            }
        }
    }, [state, highlightedIndex, handleItemSelect]);

    const dropdownContent = ((): React.ReactNode => {
        if (!isDropdownOpen) {
            return null;
        }
        if (state.kind === "idle") {
            return null;
        }
        // Popover wraps `content` in an Evergreen Card that owns the rounded
        // corners, border, shadow, and background — so we let it handle those.
        // Our inner content just lays out items. No nested rounded boxes.
        const renderMessage = (message: string) => (
            <div style={{ padding: "9px 12px" }}>
                <Text size={300} color="muted">{message}</Text>
            </div>
        );
        if (state.kind === "loading") {
            return renderMessage("Searching...");
        }
        if (state.kind === "error") {
            return renderMessage("An error occurred");
        }
        if (state.stores.length === 0) {
            return renderMessage("No results found");
        }
        return (
            <div style={{ maxHeight: 240, overflowY: "auto" }}>
                {state.stores.map((store, index) => {
                    const isLast = index === state.stores.length - 1;
                    const isHighlighted = highlightedIndex === index;
                    return (
                        <div
                            key={store.id}
                            className="jc-store-picker-item"
                            style={{
                                padding: "9px 12px",
                                cursor: "pointer",
                                borderBottom: !isLast ? `1px solid ${ColorPalette.borderColor}` : undefined,
                                background: isHighlighted ? ColorPalette.greyBackground : "transparent",
                            }}
                            onMouseEnter={() => setHighlightedIndex(index)}
                            onMouseLeave={() => setHighlightedIndex(-1)}
                            onClick={() => handleItemSelect(store)}
                            onMouseDown={(e: React.MouseEvent) => {
                                // Prevent blur from firing before onClick registers.
                                e.preventDefault();
                            }}
                        >
                            <Text size={300}>
                                <strong>{store.name}</strong>
                                <Text size={300} color="muted" marginLeft={8}>
                                    {store.id}
                                </Text>
                            </Text>
                        </div>
                    );
                })}
            </div>
        );
    })();

    return (
        <Pane ref={triggerRef} maxWidth={width}>
            <Popover
                position={Position.BOTTOM_LEFT}
                isShown={!!dropdownContent}
                content={dropdownContent ?? <Pane />}
                minWidth={triggerWidthPx || 200}
                statelessProps={{
                    width: triggerWidthPx || undefined,
                    style: { borderRadius: "10px" },
                }}
            >
                {/*
                 * Evergreen's Popover clones its single child and overrides
                 * onKeyDown (assuming the child is a button trigger). Wrapping
                 * the TextInputField in a div lets the Popover add role/aria
                 * props to the wrapper while the input keeps its own keyboard
                 * handler for ↑/↓/Enter/Esc.
                 */}
                <div>
                    <TextInputField
                        autoComplete="off"
                        autoCapitalize="off"
                        autoCorrect="off"
                        label={label}
                        required={required}
                        value={inputValue}
                        placeholder={placeholder}
                        className={className}
                        onChange={handleInputChange}
                        onFocus={handleFocus}
                        onBlur={handleBlur}
                        onClick={handleClick}
                        onKeyDown={handleKeyDown}
                    />
                </div>
            </Popover>
        </Pane>
    );
};
